import { useEffect, useState, type FormEvent } from 'react'
import type { ZebraTemplate } from '@/api/labelPrintingApi'
import { useLabelPrintingMutations, useLabelPrintingOptions } from '@/hooks/useLabelPrinting'
import {
  STARTER_ZPL,
  ZEBRA_BODY_MAX,
  checkZebraBody,
  validateZebraTemplate,
  zebraCheckSummary,
  zebraLatestVersion,
  type ZebraBodyCheck,
  type ZebraTemplateDraft,
} from '@/lib/labelPrinting'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { Field, StatusChip } from './shared'
import { ZplPreview } from './ZplPreview'

/**
 * A new template starts from the starter layout. A change starts from the
 * newest version: the open draft, else the one approved to start later, else
 * the one in use. From a draft it takes everything; from an approved version
 * only the layout, because the date and the reason belong to the new draft.
 */
function draftOf(t: ZebraTemplate | undefined, firstStock: string, firstResolution: string): ZebraTemplateDraft {
  if (!t) {
    return {
      name: '',
      labelStock: firstStock,
      resolution: firstResolution,
      body: STARTER_ZPL,
      effectiveFrom: '',
      reason: '',
    }
  }
  const open = t.draftVersion
  return {
    name: t.name,
    labelStock: t.labelStock,
    resolution: t.resolution,
    body: zebraLatestVersion(t)?.body ?? '',
    effectiveFrom: open?.effectiveDate ?? '',
    reason: open?.reason ?? '',
  }
}

const errorText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback)

/** The live check under the layout: one row per placeholder, then the other problems. */
function PlaceholderCheck({ check }: { check: ZebraBodyCheck }) {
  const rowNotes = new Set(check.rows.map(r => r.note))
  const others = check.problems.filter(p => !rowNotes.has(p))
  return (
    <div className='flex flex-col gap-2 rounded-lg border p-3' aria-live='polite'>
      <div className='flex items-center justify-between gap-2'>
        <span className='text-sm font-medium'>Placeholder check</span>
        <StatusChip {...zebraCheckSummary(check)} />
      </div>
      {check.rows.length === 0 && (
        <p className='text-sm text-muted-foreground'>No placeholders. Every value on this label is fixed text.</p>
      )}
      <ul className='flex flex-col gap-1.5'>
        {check.rows.map(r => (
          <li key={r.name} className='flex items-start gap-2 text-sm'>
            <Mark ok={r.known} />
            <span className='shrink-0 font-mono text-xs leading-5'>{`{{${r.name}}}`}</span>
            <span className={cn('text-xs leading-5', r.known ? 'text-muted-foreground' : 'text-destructive')}>
              {r.note}
            </span>
          </li>
        ))}
        {others.map(p => (
          <li key={p} className='flex items-start gap-2 text-sm'>
            <Mark ok={false} />
            <span className='text-xs leading-5 text-destructive'>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function Mark({ ok }: { ok: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-bold',
        ok ? 'bg-blue-50 text-blue-800' : 'bg-destructive/10 text-destructive',
      )}
    >
      {ok ? '✓' : '✗'}
    </span>
  )
}

/**
 * Add a Zebra template, or change one. Either way the result is a draft:
 * printers keep using the approved version until someone approves the draft.
 * The layout is checked as it is typed, and saving waits until it passes.
 */
export function ZebraTemplateFormDialog({
  template,
  onClose,
  onDone,
}: {
  template?: ZebraTemplate
  onClose: () => void
  onDone: (message: string) => void
}) {
  const options = useLabelPrintingOptions()
  const stocks = options.data?.zebraStocks ?? []
  const resolutions = options.data?.zebraResolutions ?? []
  const registry = options.data?.placeholders
  const { createZebraTemplate, saveZebraDraft, updateZebraTemplate } = useLabelPrintingMutations()

  const mode = template ? 'edit' : 'new'
  // Fixed when the form opens, so a refresh behind it (after a 409) cannot
  // change what this save says it started from.
  const [start] = useState(() => ({
    from: template ? zebraLatestVersion(template) : null,
    draft: template?.draftVersion ?? null,
  }))
  const [draft, setDraft] = useState(() => draftOf(template, stocks[0] ?? '', resolutions[0] ?? ''))
  const [touched, setTouched] = useState(false)
  const [saving, setSaving] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)

  // Options may arrive after a new draft was made: take the first size and
  // resolution then. The selects offer no empty choice, so '' means unpicked.
  const firstStock = stocks[0]
  const firstResolution = resolutions[0]
  useEffect(() => {
    if (template) return
    setDraft(d => ({
      ...d,
      labelStock: d.labelStock === '' && firstStock !== undefined ? firstStock : d.labelStock,
      resolution: d.resolution === '' && firstResolution !== undefined ? firstResolution : d.resolution,
    }))
  }, [template, firstStock, firstResolution])

  // Until the list of placeholders arrives, every one would look unknown.
  const check = registry ? checkZebraBody(draft.body, registry) : null
  const allErrors = validateZebraTemplate(draft, mode)
  const errors = touched ? allErrors : {}
  const blocked = check === null || check.problems.length > 0

  const set = <K extends keyof ZebraTemplateDraft>(key: K, value: ZebraTemplateDraft[K]) =>
    setDraft(d => ({ ...d, [key]: value }))

  async function submit(e: FormEvent) {
    e.preventDefault()
    setTouched(true)
    if (Object.keys(allErrors).length > 0 || blocked) return
    setServerError(null)
    setSaving(true)
    const name = draft.name.trim()
    // The raw `YYYY-MM-DD` from the date input.
    const version = { body: draft.body, effectiveFrom: draft.effectiveFrom, reason: draft.reason.trim() }
    try {
      if (!template) {
        await createZebraTemplate.mutateAsync({
          name,
          labelStock: draft.labelStock,
          resolution: draft.resolution,
          ...version,
        })
        onDone(`Saved “${name}” as a draft. It is not offered to any printer until it is approved.`)
        return
      }
      // The rename first: a name already in use is the likeliest refusal, and
      // then nothing else has been written.
      if (name !== template.name) {
        await updateZebraTemplate.mutateAsync({ id: template.id, payload: { name } })
      }
      // A 409 here means the draft changed since this form opened: the message
      // says so, and the hook refreshes the list.
      await saveZebraDraft.mutateAsync({
        id: template.id,
        payload: { ...version, baseChangedAt: start.draft?.changedAt ?? null },
      })
      onDone(
        start.draft
          ? `Saved the draft of “${name}”.`
          : `Saved a new draft of “${name}”. The current version stays in use until the draft is approved.`,
      )
    } catch (err) {
      setServerError(errorText(err, 'Could not save the template.'))
    } finally {
      setSaving(false)
    }
  }

  const description = !template
    ? 'It is saved as a draft. Printers can use it only after it is approved.'
    : start.draft
      ? `Starts from draft v${start.draft.version}. Saving replaces that draft, which is waiting for approval.`
      : `Starts from v${start.from?.version ?? 1}. Saving makes a new draft. Nothing changes for printers until the draft is approved.`

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className='max-h-[90vh] overflow-y-auto sm:max-w-2xl'>
        <DialogHeader>
          <DialogTitle>{template ? `Change “${template.name}”` : 'New Zebra template'}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} noValidate className='flex flex-col gap-4'>
          <Field id='zebra-template-name' label='Name' error={errors.name}>
            <Input
              id='zebra-template-name'
              value={draft.name}
              autoFocus
              autoComplete='off'
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? 'zebra-template-name-error' : undefined}
              onChange={e => set('name', e.target.value)}
            />
          </Field>

          <div className='flex gap-3'>
            <Field id='zebra-template-stock' label='Label size'>
              <Select value={draft.labelStock} disabled={!!template} onValueChange={v => set('labelStock', v)}>
                <SelectTrigger id='zebra-template-stock'>
                  <SelectValue placeholder='Select a size' />
                </SelectTrigger>
                <SelectContent>
                  {stocks.map(s => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field id='zebra-template-resolution' label='Resolution'>
              <Select value={draft.resolution} disabled={!!template} onValueChange={v => set('resolution', v)}>
                <SelectTrigger id='zebra-template-resolution'>
                  <SelectValue placeholder='Select a resolution' />
                </SelectTrigger>
                <SelectContent>
                  {resolutions.map(r => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          {template && (
            <p className='-mt-2 text-xs text-muted-foreground'>
              Size and resolution are fixed. Add a new template for another size.
            </p>
          )}

          <Field
            id='zebra-template-body'
            label='Layout'
            help='Mark each value that Smart CGT fills in with double braces, for example {{lot_num}}.'
            aside={
              <span
                className={cn(
                  'text-xs',
                  draft.body.length > ZEBRA_BODY_MAX ? 'font-semibold text-destructive' : 'text-muted-foreground',
                )}
              >
                {draft.body.length} / {ZEBRA_BODY_MAX}
              </span>
            }
          >
            <Textarea
              id='zebra-template-body'
              value={draft.body}
              rows={12}
              spellCheck={false}
              autoComplete='off'
              className='font-mono text-xs md:text-xs'
              onChange={e => set('body', e.target.value)}
            />
          </Field>

          {/* Drawn even while the check below lists problems: seeing the label helps fix them. */}
          {draft.labelStock && draft.resolution && (
            <div className='flex flex-col gap-1.5'>
              <span className='text-sm font-medium'>Preview</span>
              <ZplPreview
                body={draft.body}
                labelStock={draft.labelStock}
                resolution={draft.resolution}
                placeholders={registry}
                delayMs={500}
              />
            </div>
          )}

          {check ? (
            <PlaceholderCheck check={check} />
          ) : (
            <p className='text-sm text-muted-foreground'>Loading the placeholder list…</p>
          )}

          <Field
            id='zebra-template-effective'
            label='Effective from'
            help='Printers use this version from this day, once it is approved.'
            error={errors.effectiveFrom}
          >
            <Input
              id='zebra-template-effective'
              type='date'
              value={draft.effectiveFrom}
              className='w-48'
              aria-invalid={!!errors.effectiveFrom}
              aria-describedby={errors.effectiveFrom ? 'zebra-template-effective-error' : undefined}
              onChange={e => set('effectiveFrom', e.target.value)}
            />
          </Field>

          <Field id='zebra-template-reason' label='Reason' error={errors.reason}>
            <Textarea
              id='zebra-template-reason'
              value={draft.reason}
              rows={3}
              aria-invalid={!!errors.reason}
              aria-describedby={errors.reason ? 'zebra-template-reason-error' : undefined}
              onChange={e => set('reason', e.target.value)}
            />
          </Field>

          {serverError && (
            <p role='alert' className='text-sm text-destructive'>
              {serverError}
            </p>
          )}

          <DialogFooter>
            <Button type='button' variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button type='submit' disabled={saving || blocked}>
              {saving ? 'Saving…' : 'Save draft'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
